import moment from "moment";

export const CATEGORY_API_PARAMS = {
  out_of_spec: { sample_spec: "out_of_spec" },
  in_spec: { sample_spec: "in_spec" },
  incomplete: { sample_spec: "incomplete" },
  samples_received: { sample_spec: "" },
  samples_in_testing: { sample_spec: "in_progress" },
  results_available: { test_spec: "result_ready" },
};

export const parseDisplayDateRange = (dateRange) => {
  const [startDate, endDate] = dateRange.split(" - ");

  return {
    date_from: moment(startDate, ["MMM DD, YYYY", "MMM D, YYYY"]).format("YYYY-MM-DD"),
    date_to: moment(endDate, ["MMM DD, YYYY", "MMM D, YYYY"]).format("YYYY-MM-DD"),
  };
};

export const buildResultTestSampleFetchParams = ({
  entry,
  dateRange,
  secondaryField,
}) => {
  const params = {
    ...parseDisplayDateRange(dateRange),
    map_filter: entry.mapName,
    test_filter: entry.testName,
  };

  if (secondaryField && entry.secondaryFieldValue != null && entry.secondaryFieldValue !== "") {
    params.field_filter = secondaryField;
    params.field_filter_val = entry.secondaryFieldValue;
  }

  return params;
};

const formatMapDisplayName = (mapName) => (
  mapName === "no_map" ? "No map" : mapName
);

export const buildResultTestSampleModalTitle = (entry) => {
  const contextParts = [formatMapDisplayName(entry.mapName)];

  if (entry.secondaryFieldValue != null && entry.secondaryFieldValue !== "") {
    contextParts.push(entry.secondaryFieldValue);
  }

  return `${entry.testName} (${contextParts.join(", ")}): Total tests Requested`;
};

export const formatResultTestDateRange = (dateRange) => {
  if (!dateRange) return "-";

  const [startDate, endDate] = dateRange.split(" - ");
  const formatDate = (date) => moment(date, ["MMM DD, YYYY", "MMM D, YYYY"]).format("D MMM YYYY");

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export const CATEGORY_TO_TEST_SPEC_LABEL = {
  out_of_spec: "Out of Spec",
  in_spec: "In Spec",
  incomplete: "Incomplete",
  samples_in_testing: "In Progress",
};

const parseReceivedDate = (date) => {
  if (!date) return date;
  const parsed = moment(date, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : date;
};

const formatTestsForCategory = (tests, category) => {
  if (!Array.isArray(tests) || tests.length === 0) return "";

  const testSpecLabel = CATEGORY_TO_TEST_SPEC_LABEL[category];
  if (testSpecLabel) {
    const matchingTests = tests.filter((test) => test.test_spec === testSpecLabel);
    if (matchingTests.length > 0) {
      return matchingTests.map((test) => test.test_name).filter(Boolean).join(", ");
    }
  }

  return tests.map((test) => test.test_name).filter(Boolean).join(", ");
};

const SAMPLE_SPEC_TO_FINAL_RESULT = {
  in_spec: "In Spec",
  out_of_spec: "Out of Spec",
  incomplete: "Incomplete",
  in_progress: "In Progress",
};

const deriveFinalResult = (row) => {
  if (row.sample_spec && SAMPLE_SPEC_TO_FINAL_RESULT[row.sample_spec]) {
    return SAMPLE_SPEC_TO_FINAL_RESULT[row.sample_spec];
  }

  const tests = row.test || [];
  if (!tests.length) return "-";

  const testSpecs = tests.map((test) => test.test_spec).filter(Boolean);
  if (testSpecs.some((spec) => spec === "Out of Spec")) return "Out of Spec";
  if (testSpecs.some((spec) => spec === "In Progress" || spec === "Incomplete")) {
    return testSpecs.find((spec) => spec === "In Progress" || spec === "Incomplete");
  }
  if (testSpecs.length > 0 && testSpecs.every((spec) => spec === "In Spec")) return "In Spec";

  return testSpecs[0] || "-";
};

export const normalizeVolumeTrendSampleRows = (rows, category) => rows.map((row) => ({
  sample_id: row.sample_id,
  received_date: parseReceivedDate(row.received_date),
  sample_type: row.sample_type,
  tests: formatTestsForCategory(row.test, category),
  final_result: deriveFinalResult(row),
}));

export const normalizeAnalyticsSampleRows = (rows, category) => rows.map((row) => ({
  sample_id: row.sample_id,
  received_date: parseReceivedDate(row.received_date),
  sample_type: row.sample_type,
  tests: formatTestsForCategory(row.test, category),
}));

const normalizeTestDetails = (testDetails) => {
  const testMap = {};

  testDetails.forEach((entry) => {
    const { received_date: date, received_tests: receivedTests } = entry;

    receivedTests.forEach((testName) => {
      const count = entry[testName];
      if (!testMap[testName]) {
        testMap[testName] = { test_name: testName, details: {} };
      }
      if (testMap[testName].details[date] === undefined) {
        testMap[testName].details[date] = count;
      } else {
        testMap[testName].details[date] += count;
      }
    });
  });

  return Object.values(testMap).map((test) => ({
    ...test,
    total: Object.values(test.details).reduce((sum, count) => sum + count, 0),
  }));
};

const normalizeSpecDetails = (specDetails) => ({
  num_in_spec: specDetails.num_in_spec,
  num_out_of_spec: specDetails.num_out_of_spec,
  num_incomplete_spec: specDetails.num_incomplete,
});

const normalizeSampleSummary = (result) => {
  const { kpi_info: kpiInfo } = result;

  return {
    samples_received: kpiInfo.num_received,
    results_available: kpiInfo.num_result_available,
    samples_in_testing: kpiInfo.num_in_progress,
  };
};

export const normalizeTestResultAnalytics = (result, secondaryField = null) => {
  const normalized = result.map((entry) => {
    const resultsAvailable = entry.num_result_ready;
    const testsRequested = entry.num_requested;
    const secondaryFieldValue = secondaryField != null
      ? String(entry[secondaryField] ?? "")
      : null;

    const barKeyParts = [entry.map_title];
    if (secondaryField) {
      barKeyParts.push(secondaryFieldValue);
    }
    barKeyParts.push(entry.test_name);

    return {
      mapName: entry.map_title,
      testName: entry.test_name,
      secondaryFieldValue,
      barKey: barKeyParts.join("__"),
      resultsAvailable,
      testsRequested,
      testsInProcess: entry.num_in_progress,
      testsPending: testsRequested - resultsAvailable,
    };
  });

  return normalized.sort((a, b) => {
    if (a.mapName !== b.mapName) {
      return a.mapName.localeCompare(b.mapName);
    }

    if (secondaryField) {
      const secondaryCompare = String(a.secondaryFieldValue ?? "")
        .localeCompare(String(b.secondaryFieldValue ?? ""));
      if (secondaryCompare !== 0) {
        return secondaryCompare;
      }
    }

    return a.testName.localeCompare(b.testName);
  });
};

export const normalizeAnalyticsData = (result) => ({
  spec_details: normalizeSpecDetails(result.spec_details),
  sample_summary: normalizeSampleSummary(result),
  test_details: normalizeTestDetails(result.test_details),
  submission_details: result.submission_details,
  ratio_details: result.ratio_details,
});
